import React, { useState } from 'react';
import { useRequest } from 'ahooks';
import { executeQueryGetAuth } from 'app/utils/api';
import { Callout, Intent, Spinner, Button, Switch, H4 } from '@blueprintjs/core';
import ReactTable from 'react-table';
import { filter } from 'lodash';

export default function CoachingActivity(props) {
    const [showOnlyNotUseful, setShowOnlyNotUseful] = useState(false);

    // Get all coaching data by loading from visible classes only (excluding class 2)
    function loadAllCoachingData() {
        return new Promise((resolve, reject) => {
            try {
                // Get ALL classes first, then filter for visible enrollment and exclude class 2
                executeQueryGetAuth("class", {}, props.user, (data) => {
                    const allClasses = data.body;
                    console.log('All classes (including hidden):', allClasses.length);
                    
                    // Filter to only include classes with visible enrollment and exclude class 2
                    const filteredClasses = allClasses.filter(classData => {
                        const hasVisibleEnrollment = parseInt(classData['enrollment_hidden']) === 0;
                        const isNotClass2 = parseInt(classData.id) !== 2;
                        return hasVisibleEnrollment && isNotClass2;
                    });
                    console.log('Filtered classes (visible enrollment, not class 2):', filteredClasses.length);
                    
                    // For each filtered class, get all students and their coaching data
                    const coachingPromises = filteredClasses.map(classData => {
                        return new Promise((resolveClass, rejectClass) => {
                            executeQueryGetAuth(`class/${classData.id}/students`, {}, props.user, (studentData) => {
                                const students = studentData.body;
                                const studentCoachingPromises = students.map(student => {
                                    return new Promise((resolveStudent, rejectStudent) => {
                                        executeQueryGetAuth(`data/coaching/${student.id}`, {}, props.user, (coachingData) => {
                                            try {
                                                const coaching = JSON.parse(coachingData.text);
                                                // Add email, class info, and other info to each coaching entry
                                                if (Array.isArray(coaching)) {
                                                    return resolveStudent(coaching.map(entry => ({
                                                        ...entry,
                                                        email: student.email,
                                                        feedbackTime: entry.created || entry.feedbackTime,
                                                        classId: classData.id,
                                                        isHidden: parseInt(classData['enrollment_hidden']) !== 0
                                                    })));
                                                }
                                                resolveStudent([]);
                                            } catch (e) {
                                                resolveStudent([]);
                                            }
                                        });
                                    });
                                });
                                
                                Promise.all(studentCoachingPromises).then(allStudentCoaching => {
                                    resolveClass(allStudentCoaching.flat());
                                }).catch(rejectClass);
                            });
                        });
                    });

                    Promise.all(coachingPromises).then(allClassCoaching => {
                        const allCoaching = allClassCoaching.flat();
                        console.log('All coaching data from all classes:', allCoaching);
                        console.log('Total coaching entries:', allCoaching.length);
                        resolve(allCoaching);
                    }).catch(reject);
                });
            }
            catch (err) {
                reject(err);
            }
        });
    }

    function loadClasses() {
        return new Promise((resolve, reject) => {
            try {
                executeQueryGetAuth("class", {}, props.user, (data) => {
                    // Filter out hidden classes (enrollment_hidden != 0)
                    const visibleClasses = filter(data.body, classData => {
                        return parseInt(classData['enrollment_hidden']) == 0;
                    });
                    resolve(visibleClasses);
                });
            }
            catch (err) {
                reject(err);
            }
        });
    }

    function loadClassEnrollments() {
        return new Promise((resolve, reject) => {
            try {
                // Get all visible classes first
                loadClasses().then(visibleClasses => {
                    // Ensure visibleClasses is an array
                    const classesArray = Array.isArray(visibleClasses) ? visibleClasses : [];
                    const enrollmentPromises = classesArray.map(classData => {
                        return new Promise((resolveEnrollment, rejectEnrollment) => {
                            executeQueryGetAuth(`class/${classData.id}/students`, {}, props.user, (data) => {
                                resolveEnrollment(data.body);
                            });
                        });
                    });

                    Promise.all(enrollmentPromises).then(allEnrollments => {
                        // Flatten all enrollments into a single array
                        const allStudents = allEnrollments.flat();
                        resolve(allStudents);
                    }).catch(reject);
                }).catch(reject);
            }
            catch (err) {
                reject(err);
            }
        });
    }

    const { data: coachingData, error: coachingError, loading: coachingLoading } = useRequest(loadAllCoachingData);
    const { data: enrollmentData, error: enrollmentError, loading: enrollmentLoading } = useRequest(loadClassEnrollments);

    if (coachingLoading || enrollmentLoading) {
        return <Spinner />
    }

    if (coachingError || enrollmentError) {
        return <Callout intent={Intent.DANGER} >
            <p>Coaching Error: {JSON.stringify(coachingError)}</p>
            <p>Enrollment Error: {JSON.stringify(enrollmentError)}</p>
        </Callout>
    }

    // Calculate analytics (visible classes only, excluding class 2 - only entries with ratings - useful or not-useful)
    const allCoachingData = Array.isArray(coachingData) ? coachingData : [];
    // Filter to only include entries where users provided a rating (from visible classes, excluding class 2)
    const ratedCoachingData = allCoachingData.filter(item => 
        item.support === 'useful' || item.support === 'not-useful'
    );
    const total = ratedCoachingData.length;
    const useful = ratedCoachingData.filter(item => item.support === 'useful').length;
    const notUseful = ratedCoachingData.filter(item => item.support === 'not-useful').length;
    const usefulPercentage = total > 0 ? Math.round((useful / total) * 100) : 0;
    const notUsefulPercentage = total > 0 ? Math.round((notUseful / total) * 100) : 0;

    // Get list of emails from visible classes
    const visibleClassEmails = Array.isArray(enrollmentData) 
        ? enrollmentData.map(student => student.email).filter(email => email)
        : [];

    // Filter coaching data to only show entries from visible classes AND with ratings
    const visibleClassCoachingData = ratedCoachingData.filter(item => 
        visibleClassEmails.includes(item.email)
    );

    // Filter data - by default show only not useful, toggle to show all
    const filteredData = showOnlyNotUseful 
        ? visibleClassCoachingData
        : visibleClassCoachingData.filter(item => item.support === 'not-useful');

    const columns = [
        {
            Header: "Reaction Date",
            accessor: "feedbackTime",
            Cell: (props) => {
                if (props.value === undefined) {
                    return <span />
                }
                return <p>{props.value}</p>
            }
        },
        {
            Header: "Reaction",
            accessor: "support"
        },
        {
            Header: "User",
            accessor: "email",
            Cell: (props) => {
                return <p>{props.value}</p>
            }
        },
        {
            Header: "Coaching",
            accessor: "coaching",
            Cell: (props) => {
                return <p>{props.value}</p>
            }
        }
    ]

    return <div style={{marginBottom: '20px'}}>
        <H4>Coaching Feedback Analytics</H4>
        
        {/* Analytics Summary */}
        <Callout intent={Intent.PRIMARY} style={{marginBottom: '20px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap'}}>
                <div>
                    <strong>Total Rated Feedback (Visible Classes, Excluding Class 2):</strong> {total}
                </div>
                <div>
                    <strong>Useful:</strong> {useful} ({usefulPercentage}%)
                </div>
                <div>
                    <strong>Not Useful:</strong> {notUseful} ({notUsefulPercentage}%)
                </div>
            </div>
        </Callout>

        {/* Filter Controls */}
        <div style={{marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px'}}>
            <Switch
                checked={showOnlyNotUseful}
                onChange={(e) => setShowOnlyNotUseful(e.currentTarget.checked)}
                label="Show all feedback (including useful)"
            />
            {!showOnlyNotUseful && (
                <Button 
                    intent={Intent.WARNING} 
                    text={`${visibleClassCoachingData.filter(item => item.support === 'not-useful').length} Not Useful Entries (Visible Classes, Excluding Class 2)`}
                    disabled
                />
            )}
        </div>

        <H4>Coaching Activity</H4>
        <ReactTable
            className={"minimal-overview-table coaching-activity-table"}
            //@ts-ignore
            data={filteredData} 
            pageSize={filteredData.length} 
            showPageSizeOptions={false} 
            showPagination={false} 
            sortable={false} 
            columns={columns} />
    </div>
}
